import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  getIntegrationSheetsConfig,
  getIntegrationSheetsLogDetail,
  getIntegrationSheetsLogs,
  previewIntegrationSheets,
  syncIntegrationSheets,
  updateIntegrationSheetsConfig,
  type IntegrationSheetsConfig,
  type IntegrationSheetsLogItem,
  type IntegrationSheetsSyncResult,
} from '../../api/IntegrationSheets';

const { Text, Paragraph, Title } = Typography;

type ChangeRow = {
  article: string;
  from: number;
  to: number;
};

type Props = {
  marketplaceAuthId: number;
  marketplace: string;
};

function formatWhen(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const readChanges = (
  stats: Record<string, unknown> | null | undefined,
): ChangeRow[] => {
  const raw = stats?.changes;
  if (!Array.isArray(raw)) return [];
  const out: ChangeRow[] = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue;
    const row = c as Record<string, unknown>;
    if (typeof row.article !== 'string') continue;
    const from = Number(row.from);
    const to = Number(row.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    out.push({ article: row.article, from, to });
  }
  return out;
};

export const IntegrationSheetsTab = ({
  marketplaceAuthId,
  marketplace,
}: Props) => {
  const [config, setConfig] = useState<IntegrationSheetsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [previewSample, setPreviewSample] = useState<string[]>([]);
  const [previewSkipped, setPreviewSkipped] = useState<
    { article: string; reason: string }[]
  >([]);
  const [previewToDisable, setPreviewToDisable] = useState<number | null>(null);
  const [lastSyncResult, setLastSyncResult] =
    useState<IntegrationSheetsSyncResult | null>(null);
  const [logs, setLogs] = useState<IntegrationSheetsLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState<IntegrationSheetsLogItem | null>(
    null,
  );
  const [expandedLoading, setExpandedLoading] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState('');
  const [gid, setGid] = useState('0');
  const [minPrice, setMinPrice] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await getIntegrationSheetsConfig(marketplaceAuthId);
      setConfig(cfg);
      setEnabled(cfg.sheets_list_enabled);
      setUrl(cfg.sheets_list_url || '');
      setGid(cfg.sheets_list_gid || '0');
      setMinPrice(cfg.sheets_min_price);
    } catch (e: any) {
      message.error(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [marketplaceAuthId]);

  const reloadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await getIntegrationSheetsLogs(marketplaceAuthId, 72);
      setLogs(data.items);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [marketplaceAuthId]);

  useEffect(() => {
    void reload();
    void reloadLogs();
  }, [reload, reloadLogs]);

  const onSave = async () => {
    setSaving(true);
    try {
      const cfg = await updateIntegrationSheetsConfig(marketplaceAuthId, {
        sheets_list_enabled: enabled,
        sheets_list_url: url.trim() || null,
        sheets_list_gid: gid.trim() || '0',
        sheets_min_price: minPrice,
      });
      setConfig(cfg);
      message.success('Сохранено');
    } catch (e: any) {
      message.error(e?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const onPreview = async () => {
    setPreviewing(true);
    try {
      await updateIntegrationSheetsConfig(marketplaceAuthId, {
        sheets_list_url: url.trim() || null,
        sheets_list_gid: gid.trim() || '0',
        sheets_min_price: minPrice,
      });
      const data = await previewIntegrationSheets(marketplaceAuthId);
      setPreviewTotal(data.total);
      setPreviewSample(data.sample);
      setPreviewSkipped(data.skipped || []);
      setPreviewToDisable(data.toDisable?.length ?? 0);
      const missingCount =
        data.missing_in_catalog?.length ?? data.missingInCatalog?.length ?? 0;
      const skippedMsg =
        data.skipped?.length ? `, пропущено ${data.skipped.length}` : '';
      const disableMsg =
        data.toDisable?.length
          ? `, отключить: ${data.toDisable.length}${
              data.orphanDisables?.length
                ? ` (вне таблицы: ${data.orphanDisables.length})`
                : ''
            }`
          : '';
      const missingMsg = missingCount ? `, нет в каталоге: ${missingCount}` : '';
      message.success(
        `Включить: ${data.total}${disableMsg}${missingMsg}${skippedMsg}`,
      );
    } catch (e: any) {
      message.error(e?.message || 'Предпросмотр не удался');
    } finally {
      setPreviewing(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      const result = await syncIntegrationSheets(marketplaceAuthId, false);
      setLastSyncResult(result);
      if (result.error) {
        message.error(result.error);
      } else if (result.skipped) {
        message.info(`Пропущено: ${result.skipped}`);
      } else {
        message.success(
          `Синхронизация: +${result.rows_added} новых строк, включено ${result.set_in_sale_on}, выключено ${result.set_in_sale_off}${
            result.orphan_disables?.length
              ? ` (вне таблицы: ${result.orphan_disables.length})`
              : ''
          }`,
        );
      }
      void reload();
      void reloadLogs();
    } catch (e: any) {
      message.error(e?.message || 'Синхронизация не удалась');
    } finally {
      setSyncing(false);
    }
  };

  const onExpandLog = async (row: IntegrationSheetsLogItem) => {
    if (row.stats?.changes && Array.isArray(row.stats.changes)) {
      setExpandedLog(row);
      return;
    }
    setExpandedLoading(true);
    try {
      const detail = await getIntegrationSheetsLogDetail(row.id);
      setExpandedLog(detail);
    } catch (e: any) {
      message.error(e?.message || 'Не удалось загрузить детали');
    } finally {
      setExpandedLoading(false);
    }
  };

  const changeColumns: ColumnsType<ChangeRow> = useMemo(
    () => [
      { title: 'Артикул', dataIndex: 'article', key: 'article' },
      {
        title: 'Было in_sale',
        key: 'from',
        render: (_, r) => (r.from ? 'да' : 'нет'),
      },
      {
        title: 'Стало in_sale',
        key: 'to',
        render: (_, r) =>
          r.to ? <Tag color="green">вкл</Tag> : <Tag color="default">выкл</Tag>,
      },
    ],
    [],
  );

  const logColumns: ColumnsType<IntegrationSheetsLogItem> = useMemo(
    () => [
      {
        title: 'Время',
        key: 'finishedAt',
        render: (_, r) => formatWhen(r.finishedAt),
      },
      {
        title: 'Строк в листе',
        key: 'rowsTotal',
        render: (_, r) => {
          const stats = r.stats || {};
          const eligible =
            typeof stats.rows_eligible === 'number'
              ? stats.rows_eligible
              : r.rowsTotal;
          const inSheet =
            typeof stats.rows_in_sheet === 'number' ? stats.rows_in_sheet : '—';
          return (
            <span>
              {inSheet} / {eligible}
            </span>
          );
        },
      },
      {
        title: 'Добавлено',
        key: 'rows_added',
        render: (_, r) => {
          const added = r.stats?.rows_added;
          return typeof added === 'number' ? added : '—';
        },
      },
      {
        title: 'Изменений',
        dataIndex: 'rowsChanged',
        key: 'rowsChanged',
      },
      {
        title: '',
        key: 'actions',
        render: (_, r) => (
          <Button size="small" onClick={() => void onExpandLog(r)}>
            Детали
          </Button>
        ),
      },
    ],
    [],
  );

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const expandedChanges = expandedLog ? readChanges(expandedLog.stats) : [];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {!config?.service_account_configured ? (
        <Alert
          type="warning"
          showIcon
          message="Service Account не настроен"
          description="Задайте GOOGLE_SERVICE_ACCOUNT_JSON на сервере. Таблицу нужно расшарить на email сервисного аккаунта (Viewer)."
        />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Автосинхронизация каждые 10 минут"
          description="Таблица — единственный источник истины для in_sale: «Включить» в листе → в продаже, всё остальное (нет в листе, «Отключить», цена ниже порога) → снято. Ручное включение/выключение продажи заблокировано."
        />
      )}

      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Title level={5} style={{ marginTop: 0 }}>
          Google Таблица — {marketplace}
        </Title>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text>Синхронизация из таблицы</Text>
            <div>
              <Switch checked={enabled} onChange={setEnabled} />
            </div>
          </div>
          <div>
            <Text>Ссылка на таблицу или spreadsheet ID</Text>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
            />
          </div>
          <div>
            <Text>GID листа (если не в ссылке)</Text>
            <Input
              value={gid}
              onChange={(e) => setGid(e.target.value)}
              placeholder="1914448134"
              style={{ maxWidth: 240 }}
            />
          </div>
          <div>
            <Text>Минимальная цена (опционально)</Text>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              Используется только в legacy-режиме (без колонки «Статус ФБС»).
              Фильтрует строки по «ПАРСИНГ ЦЕН» или «Цена на ВБ с кошельком».
            </Paragraph>
            <InputNumber
              min={0}
              value={minPrice ?? undefined}
              onChange={(v) => setMinPrice(v == null ? null : Number(v))}
              placeholder="не задано"
              style={{ width: 160 }}
            />
          </div>
          <Space wrap>
            <Button type="primary" loading={saving} onClick={() => void onSave()}>
              Сохранить
            </Button>
            <Button loading={previewing} onClick={() => void onPreview()}>
              Предпросмотр
            </Button>
            <Button
              type="primary"
              ghost
              loading={syncing}
              disabled={!enabled}
              onClick={() => void onSync()}
            >
              Синхронизировать сейчас
            </Button>
          </Space>
          {config?.sheets_last_sync_at ? (
            <Text type="secondary">
              Последняя синхронизация: {formatWhen(config.sheets_last_sync_at)}
            </Text>
          ) : null}
          {previewTotal != null ? (
            <Alert
              type="success"
              message={`Включить: ${previewTotal}${
                previewToDisable != null && previewToDisable > 0
                  ? `, отключить: ${previewToDisable}`
                  : ''
              }${
                previewSkipped.length
                  ? `, пропущено: ${previewSkipped.length}`
                  : ''
              }`}
              description={
                <>
                  {previewSample.length ? (
                    <div>
                      Примеры (включить):{' '}
                      {previewSample.slice(0, 15).join(', ')}
                      {previewSample.length > 15 ? '…' : ''}
                    </div>
                  ) : null}
                  {previewSkipped.length ? (
                    <div style={{ marginTop: 8 }}>
                      Пропущенные:{' '}
                      {previewSkipped
                        .slice(0, 5)
                        .map((s) => `${s.article} (${s.reason})`)
                        .join('; ')}
                      {previewSkipped.length > 5 ? '…' : ''}
                    </div>
                  ) : null}
                </>
              }
            />
          ) : null}
        </Space>
      </Card>

      {lastSyncResult && !lastSyncResult.error ? (
        <Card bordered={false} style={{ borderRadius: 8 }} title="Последний прогон">
          <Space direction="vertical">
            <Text>
              Строк в листе: {lastSyncResult.rows_in_sheet}, подходит:{' '}
              {lastSyncResult.rows_eligible}, новых: {lastSyncResult.rows_added}
            </Text>
            <Text>
              FBS включено: {lastSyncResult.enabled_fbs}, in_sale вкл:{' '}
              {lastSyncResult.set_in_sale_on}, выкл:{' '}
              {lastSyncResult.set_in_sale_off}
            </Text>
            {lastSyncResult.missing_in_catalog.length ? (
              <Text type="warning">
                Нет в каталоге ({lastSyncResult.missing_in_catalog.length}):{' '}
                {lastSyncResult.missing_in_catalog.slice(0, 10).join(', ')}
                {lastSyncResult.missing_in_catalog.length > 10 ? '…' : ''}
              </Text>
            ) : null}
            {lastSyncResult.in_sale_not_applied?.length ? (
              <Text type="warning">
                in_sale не применился ({lastSyncResult.in_sale_not_applied.length}):{' '}
                {lastSyncResult.in_sale_not_applied.slice(0, 10).join(', ')}
                {lastSyncResult.in_sale_not_applied.length > 10 ? '…' : ''}
              </Text>
            ) : null}
            {lastSyncResult.below_price_threshold?.length ? (
              <Text type="warning">
                Ниже порога ({lastSyncResult.below_price_threshold.length}):{' '}
                {lastSyncResult.below_price_threshold.slice(0, 10).join(', ')}
                {lastSyncResult.below_price_threshold.length > 10 ? '…' : ''}
              </Text>
            ) : null}
            {lastSyncResult.skipped_rows?.length ? (
              <Text type="secondary">
                Пропущено строк: {lastSyncResult.skipped_rows.length}
              </Text>
            ) : null}
            {lastSyncResult.patch_errors?.length ? (
              <Text type="warning">
                Ошибки применения: {lastSyncResult.patch_errors.length}
              </Text>
            ) : null}
          </Space>
          {lastSyncResult.changes.length ? (
            <Table
              size="small"
              style={{ marginTop: 12 }}
              rowKey="article"
              pagination={{ pageSize: 20 }}
              columns={changeColumns}
              dataSource={lastSyncResult.changes.map((c) => ({
                article: c.article,
                from: c.from ? 1 : 0,
                to: c.to ? 1 : 0,
              }))}
            />
          ) : (
            <Text type="secondary">Изменений in_sale не было</Text>
          )}
        </Card>
      ) : null}

      <Card bordered={false} style={{ borderRadius: 8 }} title="Журнал синхронизаций">
        <Table
          size="small"
          rowKey="id"
          loading={logsLoading}
          columns={logColumns}
          dataSource={logs}
          pagination={{ pageSize: 10 }}
        />
        {expandedLog ? (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>
              Изменения от {formatWhen(expandedLog.finishedAt)}
            </Title>
            {expandedLoading ? (
              <Spin />
            ) : expandedChanges.length ? (
              <Table
                size="small"
                rowKey="article"
                pagination={{ pageSize: 20 }}
                columns={changeColumns}
                dataSource={expandedChanges}
              />
            ) : (
              <Text type="secondary">Нет детализации изменений</Text>
            )}
          </div>
        ) : null}
      </Card>
    </Space>
  );
};

export default IntegrationSheetsTab;
